import { WefatComponentRuntimeError } from './component-error.js';
function selectorError(message, remediation) {
    return new WefatComponentRuntimeError('WF5005', message, remediation);
}
function defaultSchedule(callback) {
    queueMicrotask(callback);
}
function rectFor(element) {
    if (element === null)
        return null;
    const rect = element.getBoundingClientRect();
    const dataset = {};
    if (typeof HTMLElement !== 'undefined' && element instanceof HTMLElement) {
        for (const [key, value] of Object.entries(element.dataset)) {
            if (value !== undefined)
                dataset[key] = value;
        }
    }
    return Object.freeze({
        bottom: rect.bottom,
        dataset: Object.freeze(dataset),
        height: rect.height,
        id: element.getAttribute('id') ?? '',
        left: rect.left,
        right: rect.right,
        top: rect.top,
        width: rect.width,
    });
}
export function createWefatSelectorQuery(root, schedule = defaultSchedule, viewport = () => Object.freeze({
    scrollLeft: typeof window === 'undefined' ? 0 : window.scrollX,
    scrollTop: typeof window === 'undefined' ? 0 : window.scrollY,
})) {
    if (typeof root !== 'function' || typeof schedule !== 'function' || typeof viewport !== 'function') {
        throw selectorError('Selector query requires root and scheduling functions', 'Create selector queries through the generated page instance instead of constructing them manually.');
    }
    let operations = [];
    const query = Object.freeze({
        select(selector) {
            if (typeof selector !== 'string' || selector.length === 0) {
                throw selectorError('Selector query select requires a non-empty selector string', 'Pass a page-scoped ID, class, attribute, or element selector to select(...).');
            }
            return Object.freeze({
                boundingClientRect(callback) {
                    if (callback !== undefined && typeof callback !== 'function') {
                        throw selectorError('boundingClientRect callback must be a function when provided', 'Pass a function or read the result array supplied to exec(...).');
                    }
                    operations.push({ ...(callback === undefined ? {} : { callback }), kind: 'rect', selector });
                    return query;
                },
            });
        },
        selectViewport() {
            return Object.freeze({
                scrollOffset(callback) {
                    if (callback !== undefined && typeof callback !== 'function') {
                        throw selectorError('scrollOffset callback must be a function when provided', 'Pass a function or read the viewport result supplied to exec(...).');
                    }
                    operations.push({ ...(callback === undefined ? {} : { callback }), kind: 'viewport' });
                    return query;
                },
            });
        },
        exec(callback) {
            if (callback !== undefined && typeof callback !== 'function') {
                throw selectorError('Selector query exec callback must be a function when provided', 'Pass a function that accepts the ordered selector result array.');
            }
            const pending = operations;
            operations = [];
            const container = root();
            let results;
            try {
                results = Object.freeze(pending.map((operation) => operation.kind === 'viewport'
                    ? Object.freeze({ ...viewport() })
                    : rectFor(container?.querySelector(operation.selector) ?? null)));
            }
            catch (error) {
                throw selectorError(`Selector query could not evaluate a source selector: ${error instanceof Error ? error.message : String(error)}`, 'Use a browser-valid selector supported by querySelector and keep it inside the active page.');
            }
            schedule(() => {
                pending.forEach((operation, index) => {
                    if (operation.kind === 'viewport') {
                        operation.callback?.(results[index]);
                    }
                    else {
                        operation.callback?.(results[index]);
                    }
                });
                callback?.(results);
            });
        },
    });
    return query;
}
function activePageRoot(route) {
    if (typeof document === 'undefined') {
        throw selectorError('Selector query requires a browser document', 'Run the generated page in a browser or inject a DOM-backed component environment for tests.');
    }
    return [...document.querySelectorAll('[data-wefat-page]')].find((element) => element.getAttribute('data-wefat-page') === route) ?? null;
}
export function installWefatPageDomApis(page) {
    if (Object.prototype.hasOwnProperty.call(page, 'createSelectorQuery')) {
        throw selectorError('Page instance already defines createSelectorQuery', 'Rename the source page method because createSelectorQuery is reserved by the browser page facade.');
    }
    Object.defineProperty(page, 'createSelectorQuery', {
        configurable: false,
        enumerable: true,
        value: () => createWefatSelectorQuery(() => activePageRoot(page.route)),
        writable: false,
    });
}
//# sourceMappingURL=selector-query.js.map